/**
 * The ONE place a promote press is dispatched, and the sentences it refuses with.
 *
 * `core/promote.ts` emits data — a press is a descriptor, never a closure — so
 * nothing there can spend (AGENTS.md invariant 9). This is the other half: the
 * function a person's click arrives at, deliberately the only one, so a press
 * cannot come to mean one thing under the mouse and something else the day a
 * keyboard route is added. It is `takeCueAction`'s shape, for `takeCueAction`'s
 * stated reason.
 *
 * A `.ts` file rather than a component, so both halves can be tested without a
 * renderer, and so the refusal the row prints and the refusal the toast
 * says are provably the same string.
 */

import { providerFor, resolveFor, useStore } from '../core/store'
import { reasonKeyLine } from './pressName'
import { promotesFor, type PromoteKind } from '../core/promote'
import { hasDemoReserve } from '../engine/demo'
import { buildPromoteExtra } from '../engine/prompts'
import { refsToLabels } from '../engine/refs'
import { runOp } from '../engine/reason'
import { scrollToOutcomeForm } from './Grounding'
import { scrollToRewriteEditor } from './NodeCard'

/** The store's shape, without exporting `State` from the store for it — the
 *  borrow `coachView.ts` makes for the same reason. */
type StoreState = ReturnType<typeof useStore.getState>

/** Everything a refusal reads. Narrow on purpose: this answers a question about
 *  keys, locks and one node, and a wider view would invite it to answer others. */
export type PromoteView = Pick<
  StoreState,
  'modelId' | 'keys' | 'customBase' | 'customModel' | 'roleModels' | 'busy' | 'session'
>

/** What a press was pressed under: the reader's words and the answer they read. */
export interface PromoteTurn {
  question: string
  answer: string
}

/**
 * Why this press cannot run, in the sentence the reader gets — or null when it
 * can.
 *
 * `cueRefusal`'s job, on this set, and it exists for that function's reason: a
 * press that quietly does nothing is the confusion the coach's refusals were
 * written to end.
 *
 * ONE OF THESE IS NOT A COPY of the coach's. `cueRefusal` lets a keyless press
 * through when the demo holds a pre-authored branch for it, because unfurling
 * one IS the demo. Here that would be a lie: `runOp` reveals reserved children
 * written months ago and never reads `extra` on that path, so a press labelled
 * "told what the answer above argued" would hand back a branch that has never
 * heard of it. The offer is the promise, so this refuses instead.
 */
export function promoteRefusal(s: PromoteView, kind: PromoteKind, nodeId: string): string | null {
  const node = s.session.nodes[nodeId]
  // Pruned between the render that offered the press and the click that took
  // it. Rare, and the alternative is a press that lands on nothing.
  if (!node) return 'That node is no longer on the graph.'

  // Not on offer for this node — which today means one thing, the seed. It has
  // no revision editor and no outcome form, so a press for either would poke a
  // signal at a card that renders neither, and a poke that opens nothing is
  // silent by construction. ASKED of `promotesFor` rather than re-derived here:
  // that function owns which presses a node gets, and a second copy of the rule
  // is how the surface and the press come to disagree about one node.
  if (!promotesFor({ kind: 'node', isRoot: node.parentId === null }).some((p) => p.kind === kind)) {
    return 'The seed is the question this exploration asks, not a claim to rewrite or record a result against. Press one of these on something derived from it.'
  }

  switch (kind) {
    case 'challenge':
    case 'deepen':
    case 'diverge': {
      // `runOp`'s own first guard, quoted so the row greys out for the reason
      // the toast would have given.
      //
      // And it REFUSES under the lock where every other Deepen and Challenge
      // now waits on the Up next line (D162) — deliberately. D42's placement
      // rule is that a priced press never reaches past what is directly above
      // it: this row renders only while the last thing in the feed is the
      // finished answer it carries. A queued promote would run after the pass
      // in flight had landed between that answer and its press, carrying an
      // answer that is no longer the tail, onto a graph the answer never saw —
      // the one reach this row exists to forbid. So it waits for the reader to
      // press it again, over an answer that is still the last thing said.
      if (s.busy) return 'One thought at a time — let the current branch finish.'
      if (resolveFor(s, 'reason')) return null
      if (hasDemoReserve(nodeId, kind)) {
        return `The demo's branches were written in advance, so they cannot be told what this answer argued. Add your ${providerFor(s, 'reason').name} key to run a real pass from it.`
      }
      // Invariant 4: a routed role with no key refuses and never falls back to
      // the selection, so this names the model and the house the pass would
      // actually go to — in D202's sentence, the one the key card prints when
      // `runOp` refuses for the same route. It said "runs on the model you
      // selected" while naming the route's company, which under routing is a
      // model the reader did not select.
      return reasonKeyLine(s)
    }

    case 'rewrite':
      // Gated on neither a key nor the lock: the editor is a text box.
      // `reviseNode` asks for a key when the reader presses Re-derive, and that
      // press waits on the Up next line while another pass holds the lock
      // (`pressRewrite`) — the moments each is needed and can be explained.
      return null

    case 'record':
      // No busy gate and no key gate. This form is the reader typing what they
      // saw happen; it costs nothing and interrupts nothing, and the thread
      // above it says in as many words that a run and this channel do not share
      // a turn.
      return null
  }
}

/**
 * The press.
 *
 * Selection first, for every one of the four, and it is load-bearing rather
 * than tidy: a conversation is PINNED and does not follow the selection, so a
 * detached one is routinely about a node the reader is not standing on. Without
 * this, Deepen grows a branch somewhere off screen and Rewrite pokes its signal
 * at whichever card the thread has mounted — which is the selected node's, not
 * this one's. `revealNode` comes with it because the pin may sit inside a folded
 * branch, where selecting alone puts the reader on something they cannot see.
 *
 * Then each press walks through the door that already existed. Nothing here is
 * a new way to change the graph.
 */
export function takePromote(kind: PromoteKind, nodeId: string, turn: PromoteTurn): void {
  const s = useStore.getState()

  const refusal = promoteRefusal(s, kind, nodeId)
  if (refusal) {
    // Said out loud. The row is already disabled and this same sentence is
    // already printed under it, so this is for a press that was legal when it
    // rendered and is not by the time it lands — and for any future route in
    // that has no row to grey out.
    s.toast(refusal, 'warn')
    return
  }

  s.revealNode(nodeId)
  s.select(nodeId)

  switch (kind) {
    case 'challenge':
    case 'deepen':
    case 'diverge':
      // The rail and the tab, as the coach does before a pass: the nodes are
      // about to arrive in the thread, and a reader left in Breakthroughs would
      // watch nothing happen. The transcript survives the unmount — it lives in
      // module scope precisely so a change of home cannot cost an answer.
      s.setRail(true)
      s.setRightTab('node')
      // THE WHOLE DESIGN, in one argument: the answer goes in as the pass's
      // input, under a block that says it has been checked against nothing. What
      // lands is this pass's own reasoning, at this pass's own price.
      //
      // As the reader read it: each mark the answer put after a sentence is the
      // label they saw there, not the id it carries (D295) — a pass reads the
      // graph by label, and its contract keeps bracketed ids for the machine.
      void runOp(
        kind,
        nodeId,
        buildPromoteExtra(turn.question, refsToLabels(turn.answer, s.session.nodes)),
      )
      return

    // The two openers, both poked AFTER the selection above has been committed.
    // See `afterSelection`.
    case 'rewrite':
      afterSelection(nodeId, () => {
        useStore.getState().pokeRewrite()
        // And the box has to arrive on screen. The thread renders the card
        // BEFORE the conversation, so a press made from down in the
        // conversation opens this a long way above the reader.
        scrollToRewriteEditor(nodeId)
      })
      return

    case 'record':
      afterSelection(nodeId, () => {
        useStore.getState().pokeRecord()
        scrollToOutcomeForm(nodeId)
      })
      return
  }
}

/**
 * Run something one frame after the selection above has been painted.
 *
 * THE POKE CANNOT SHARE A TICK WITH THE SELECTION, and review found the case
 * that proves it. Both signals are consumed by reacting to a CHANGE in a
 * counter, against a value each block records when it mounts — which is what
 * stops a poke sent before a block existed from opening a form nobody asked
 * for. But a block can be mounted BY the very selection this press makes:
 * `OutcomeBlock` renders behind `!isRoot`, so with the seed selected there is
 * no block for any other node, and a detached conversation is routinely pinned
 * to one. Selecting and poking in one tick then delivered both in a single
 * commit: the block mounted with the counter ALREADY bumped, recorded that as
 * the value it had seen, and swallowed the signal. Measured, before this: the
 * card appeared and the form did not open.
 *
 * The rail is the same trap by another road — the whole pane is behind
 * `railRight`, and a poke turns that on, so a press made while it is shut mounts
 * the card and loses the signal in exactly the same way.
 *
 * A frame is the whole fix: the mount happens in this commit, the counter moves
 * in the next, and the block sees a change from the value it recorded. Nothing
 * about the signals themselves has to change, which is worth more than it looks
 * — the alternative was a second field naming the target, and a name that
 * outlives the press it belongs to reopens the form on every later remount.
 *
 * BUT A FRAME IS A WINDOW, and review found what fits through it. A poke is a
 * BROADCAST — whichever card is mounted consumes it, by design, because that is
 * how the E and R keys reach the selection — so a press whose frame lands after
 * the reader has gone somewhere else opens a box on a node they never pressed,
 * while the scroll goes looking for the one they did. Two input events inside
 * one frame is all it takes. So the target is captured and re-checked here, and
 * a press the world has moved past is abandoned rather than delivered to
 * whoever is standing there now. Silently: the reader moved on, and a toast
 * about a press they have already walked away from is noise.
 *
 * Exported because the coach's record cue defers for the same reason and would
 * otherwise carry a second copy of this — the shape this file keeps refusing.
 */
export function afterSelection(nodeId: string, fn: () => void): void {
  const sessionAtPress = useStore.getState().session.id
  requestAnimationFrame(() => {
    const s = useStore.getState()
    if (s.selectedId !== nodeId || s.session.id !== sessionAtPress) return
    fn()
  })
}
